import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Pencil, Trash2, KeyRound } from 'lucide-react';
import DataTable from '../../components/admin/DataTable';
import UserForm from '../../components/admin/UserForm';
import AccountPicker from '../../components/admin/AccountPicker';
import SendPasswordLinkDialog, { InviteNotice } from '../../components/admin/SendPasswordLinkDialog';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import { RoleBadge, UserStatusBadge, userStatusKey } from '../../components/admin/StatusBadge';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

export default function Users() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [sendingLink, setSendingLink] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [inviteNotice, setInviteNotice] = useState(null); // { email, result }
  // Account filter for the user list. AccountPicker stores the id; we
  // filter the loaded users client-side. Replaces the previous DataTable
  // static-options dropdown which was bounded to whatever the first page
  // of /api/admin/accounts returned (~100 of 1,899 in prod).
  const [accountFilterId, setAccountFilterId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      // Users list only — accounts no longer pre-fetched here. The form
      // uses AccountPicker (server-side searched) and the filter above
      // does the same.
      const usersData = await api.get('/api/admin/users');
      setUsers(usersData.users || []);
    } catch (err) {
      setLoadError(err?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onSaved = (savedUser, invite) => {
    setUsers((prev) => {
      const idx = prev.findIndex((u) => u.id === savedUser.id);
      if (idx === -1) return [savedUser, ...prev];
      const copy = prev.slice();
      copy[idx] = { ...copy[idx], ...savedUser };
      return copy;
    });
    if (invite) setInviteNotice({ email: savedUser.email, result: invite });
  };

  // A new link changes the "Invited / Invite expired" status: refetch.
  const onLinkSent = () => { load(); };

  const onDelete = async (u) => {
    await api.delete(`/api/admin/users/${u.id}`);
    setUsers((prev) => prev.filter((x) => x.id !== u.id));
  };

  const filters = useMemo(
    () => [
      {
        key: 'role',
        label: 'roles',
        options: [
          { value: 'nexsav_admin', label: 'Admins' },
          { value: 'ops', label: 'Operations' },
          { value: 'client', label: 'Clients' },
          { value: 'media', label: 'Media' },
        ],
      },
      {
        key: 'status',
        label: 'status',
        options: [
          { value: 'active', label: 'Active' },
          { value: 'invited', label: 'Invited' },
          { value: 'disabled', label: 'Disabled' },
        ],
      },
    ],
    []
  );

  // Apply account filter client-side before passing rows to DataTable.
  const augmented = useMemo(
    () =>
      users
        .filter((u) => !accountFilterId || u.accountId === accountFilterId)
        .map((u) => ({
          ...u,
          isActive: Boolean(u.isActive),
          status: userStatusKey({ ...u, isActive: Boolean(u.isActive) }),
        })),
    [users, accountFilterId]
  );

  const columns = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      render: (row) => (
        <div>
          <div className="text-white font-medium flex items-center gap-2">
            {row.name}
            {row.id === currentUser?.id && (
              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#B0FC38]/[0.15] border border-[#B0FC38]/30 text-[#B0FC38] tracking-wider uppercase">
                You
              </span>
            )}
          </div>
          <div className="text-[11px] text-gray-500 mt-0.5">{row.email}</div>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      sortable: true,
      render: (row) => <RoleBadge role={row.role} />,
    },
    {
      key: 'accountName',
      header: 'Account',
      sortable: true,
      render: (row) =>
        row.accountName ? (
          <span className="text-[13px] text-gray-300">{row.accountName}</span>
        ) : (
          <span className="text-[13px] text-gray-600">All (internal)</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <UserStatusBadge user={row} />,
    },
    {
      key: 'billingAccess',
      header: 'Billing',
      render: (row) =>
        row.role === 'client' ? (
          row.billingAccess ? (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#B0FC38]/[0.1] border border-[#B0FC38]/30 text-[#B0FC38] uppercase tracking-wider">
              Yes
            </span>
          ) : (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/[0.04] border border-white/[0.08] text-gray-500 uppercase tracking-wider">
              No
            </span>
          )
        ) : (
          <span className="text-[11px] text-gray-600">—</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (row) => (
        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => {
              setEditing(row);
              setFormOpen(true);
            }}
            className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.05] transition-colors"
            title="Edit"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => setSendingLink(row)}
            disabled={!row.isActive}
            className="p-1.5 rounded-lg text-gray-500 hover:text-[#B0FC38] hover:bg-[#B0FC38]/[0.08] transition-colors disabled:opacity-30 disabled:pointer-events-none"
            title={!row.isActive ? 'Re-enable this user to send a link' : row.passwordSet === false ? 'Resend invite' : 'Send password reset link'}
            aria-label={row.passwordSet === false ? `Resend invite to ${row.name}` : `Send password reset link to ${row.name}`}
          >
            <KeyRound className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => setConfirmDelete(row)}
            disabled={row.id === currentUser?.id}
            className="p-1.5 rounded-lg text-gray-500 hover:text-red-400 hover:bg-red-500/[0.05] transition-colors disabled:opacity-30 disabled:pointer-events-none"
            title={row.id === currentUser?.id ? "You can't delete your own account" : 'Delete'}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-white tracking-tight"><span className="serif-acc accent-text">Users</span></h1>
          <p className="text-sm text-gray-500 mt-1">
            Client users, NexSav admins, and operations staff. Clients see only their account.
          </p>
        </div>
        <button
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#B0FC38] text-gray-900 text-sm font-semibold hover:bg-[#9BE22E] shadow-lg shadow-[#B0FC38]/20 transition-all"
        >
          <Plus className="w-4 h-4" />
          New user
        </button>
      </div>

      {loadError && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-red-500/[0.08] border border-red-500/20 text-[13px] text-red-300/90">
          {loadError}
        </div>
      )}

      <div className="mb-4 max-w-md">
        <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-1.5">Filter by account</div>
        <AccountPicker
          value={accountFilterId}
          onChange={(id) => setAccountFilterId(id || '')}
          placeholder="Search to filter by account…"
        />
      </div>

      <DataTable
        columns={columns}
        rows={augmented}
        searchableKeys={['name', 'email', 'accountName']}
        filters={filters}
        defaultSort={{ key: 'name', direction: 'asc' }}
        loading={loading}
        emptyMessage="No users yet."
        onRowClick={(row) => {
          setEditing(row);
          setFormOpen(true);
        }}
      />

      <UserForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={onSaved}
        initial={editing}
      />

      <SendPasswordLinkDialog
        open={Boolean(sendingLink)}
        user={sendingLink}
        onClose={() => setSendingLink(null)}
        onSent={onLinkSent}
      />

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title="Delete user?"
        description={
          confirmDelete
            ? `This will permanently delete ${confirmDelete.name} (${confirmDelete.email}) and revoke all their sessions. Their account's tickets and edits will remain but attributions stay.`
            : ''
        }
        confirmLabel="Delete user"
        destructive
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => onDelete(confirmDelete)}
      />

      <InviteNotice notice={inviteNotice} onClose={() => setInviteNotice(null)} />
    </div>
  );
}
